import type MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';
import type * as mapboxgl from 'mapbox-gl';
import type { Feature, FeatureCollection } from 'geojson';
import { isCircleFeature } from './map-geometry.utils';
import type { MapDrawInteractionState } from './map-draw-interaction.controller';
import type { MapHintMode } from './map-hint.controller';

export interface MapFeatureFocusContext {
  map: () => mapboxgl.Map;
  draw: () => MapboxDraw;
  state: MapDrawInteractionState;
  setHint: (mode: MapHintMode) => void;
  refreshCtrl: () => void;
  scrollToPolygon: (id: string) => void;
  openPointEditor: (id: string) => void;
  openCoordinates: () => void;
  showErr: (key: string, error?: unknown) => void;
}

export class MapFeatureFocusController {
  constructor(private readonly context: MapFeatureFocusContext) {}

  focusCanvas(): void {
    try {
      (this.context.map()?.getCanvas() as HTMLCanvasElement)?.focus();
    } catch {}
  }

  fitBoundsOf(geojson: Feature | FeatureCollection): void {
    const bb = turf.bbox(geojson as any) as [number, number, number, number];
    if (!bb?.every(Number.isFinite)) return;
    this.context.map().fitBounds(
      [
        [bb[0], bb[1]],
        [bb[2], bb[3]],
      ],
      { padding: 40 }
    );
  }

  zoomTo(id: string): void {
    try {
      const f = this.context.draw().get(id) as Feature | undefined;
      if (!f) return;
      if (f.geometry.type === 'Point') {
        const map = this.context.map();
        map.flyTo({ center: f.geometry.coordinates as [number, number], zoom: Math.max(map.getZoom(), 15) });
        return;
      }
      this.fitBoundsOf(f);
    } catch {}
  }

  selectPoint(id: string): void {
    this.context.draw().changeMode('simple_select', { featureIds: [id] });
    this.context.state.isDrawing = false;
    this.context.state.isEditing = false;
    this.context.setHint('idle');
    this.context.refreshCtrl();
    this.context.scrollToPolygon(id);
  }

  focusZoomEdit(id: string): void {
    if (this.isPoint(id)) this.selectPoint(id);
    else this.selectPolygon(id);
    this.zoomTo(id);
    this.context.scrollToPolygon(id);
  }

  focusZoomEditAndShowCoords(id: string): void {
    if (this.isPoint(id)) {
      this.zoomTo(id);
      this.context.openPointEditor(id);
      this.selectPoint(id);
      return;
    }
    this.selectPolygon(id);
    this.zoomTo(id);
    this.context.scrollToPolygon(id);
    this.context.openCoordinates();
  }

  private isPoint(id: string): boolean {
    return this.context.draw().get(id)?.geometry.type === 'Point';
  }

  private selectPolygon(id: string): void {
    try {
      const draw = this.context.draw() as any;
      const f = draw.get(id) as Feature | undefined;
      if (!f || f.geometry.type === 'Point') return;
      const circle = isCircleFeature(f);
      if (circle) draw.changeMode('simple_select', { featureIds: [id] });
      else draw.changeMode('direct_select', { featureId: id });
      this.context.state.isEditing = !circle;
      this.context.setHint(circle ? 'idle' : 'edit');
      this.context.refreshCtrl();
      this.focusCanvas();
    } catch (err) {
      this.context.showErr('Could_not_select_polygon_tr', err);
    }
  }
}
